import React, { useState, useEffect } from 'react';
import AdminNav from '../components/AdminNav';
import { Plus, Edit2, Trash2, X, LogOut, FileText, Mail, Phone, MapPin, Map } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { sitemapLinkService, contactInfoService, siteContentService, authService } from '../services/api';

function AdminInformation() {
  const navigate = useNavigate();

  const loadContent = async (key, setter, defaultValue, isJson = false) => {
    try {
      const response = await siteContentService.get(key);
      if (response.data && response.data.value) {
        const value = isJson ? JSON.parse(response.data.value) : response.data.value;
        setter(value);
        return value;
      }
    } catch (err) {
      console.error(`Error loading ${key}:`, err);
    }
    setter(defaultValue);
    return defaultValue;
  };

  const saveContent = async (key, value, isJson = false) => {
    try {
      await siteContentService.set(key, { value: isJson ? JSON.stringify(value) : value });
    } catch (err) {
      console.error(`Error saving ${key}:`, err);
    }
  };
  
  const [contactInfo, setContactInfo] = useState({
    email: '',
    phone: '',
    address: '',
    workingHours: '',
  });
  const [editingContact, setEditingContact] = useState(false);
  const [tempContact, setTempContact] = useState(contactInfo);
  const [contactInfoId, setContactInfoId] = useState(null);
  const [contactError, setContactError] = useState('');

  const [privacyPolicy, setPrivacyPolicy] = useState('');
  const [editingPrivacy, setEditingPrivacy] = useState(false);
  const [tempPrivacy, setTempPrivacy] = useState('');

  const [faqs, setFaqs] = useState([]);

  const [showFaqForm, setShowFaqForm] = useState(false);
  const [editingFaqId, setEditingFaqId] = useState(null);
  const [faqForm, setFaqForm] = useState({
    question: '',
    answer: '',
  });

  const [sitemapLinks, setSitemapLinks] = useState([]);
  const [loadingSitemap, setLoadingSitemap] = useState(true);
  const [showSitemapForm, setShowSitemapForm] = useState(false);
  const [editingSitemapId, setEditingSitemapId] = useState(null);
  const [sitemapForm, setSitemapForm] = useState({
    category: '',
    label: '',
    path: '',
  });
  const [sitemapError, setSitemapError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const sitemapResponse = await sitemapLinkService.getAll();
        if (sitemapResponse.data) {
          setSitemapLinks(sitemapResponse.data);
        }
      } catch (err) {
        setSitemapError('خطا در دریافت نقشه سایت');
      } finally {
        setLoadingSitemap(false);
      }

      try {
        const contactResponse = await contactInfoService.get();
        if (contactResponse.data && contactResponse.data.id) {
          setContactInfoId(contactResponse.data.id);
          const data = {
            email: contactResponse.data.email || '',
            phone: contactResponse.data.phone || '',
            address: contactResponse.data.address || '',
            workingHours: contactResponse.data.workingHours || '',
          };
          setContactInfo(data);
          setTempContact(data);
        }
      } catch (err) {
        setContactError('خطا در دریافت اطلاعات تماس');
      }

      await loadContent('INFORMATION_PRIVACY', setPrivacyPolicy, privacyPolicy, false);
      const faqData = await loadContent('INFORMATION_FAQS', setFaqs, faqs, true);
      if (faqData && Array.isArray(faqData)) {
        setFaqs(faqData);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    authService.logout();
    navigate('/admin-login');
  };

  // Contact handlers
  const handleSaveContact = async () => {
    try {
      if (contactInfoId) {
        await contactInfoService.update(contactInfoId, tempContact);
      } else {
        const response = await contactInfoService.create(tempContact);
        setContactInfoId(response.data.id);
      }
      setContactInfo(tempContact);
      setEditingContact(false);
      setContactError('');
    } catch (err) {
      setContactError('خطا در ذخیره اطلاعات تماس');
    }
  };

  const handleCancelContact = () => {
    setTempContact(contactInfo);
    setEditingContact(false);
  };

  // Privacy handlers
  const handleSavePrivacy = async () => {
    setPrivacyPolicy(tempPrivacy);
    await saveContent('INFORMATION_PRIVACY', tempPrivacy, false);
    setEditingPrivacy(false);
  };

  const handleCancelPrivacy = () => {
    setTempPrivacy(privacyPolicy);
    setEditingPrivacy(false);
  };

  // FAQ handlers
  const handleAddFaq = async (e) => {
    e.preventDefault();

    let updatedFaqs;
    if (editingFaqId) {
      updatedFaqs = faqs.map(faq =>
        faq.id === editingFaqId
          ? { ...faq, ...faqForm }
          : faq
      );
      setEditingFaqId(null);
    } else {
      const newFaq = {
        id: Math.max(...faqs.map(f => f.id), 0) + 1,
        ...faqForm,
        expanded: false,
      };
      updatedFaqs = [...faqs, newFaq];
    }

    setFaqs(updatedFaqs);
    await saveContent('INFORMATION_FAQS', updatedFaqs, true);

    setFaqForm({
      question: '',
      answer: '',
    });
    setShowFaqForm(false);
  };

  const handleEditFaq = (faq) => {
    setFaqForm({
      question: faq.question,
      answer: faq.answer,
    });
    setEditingFaqId(faq.id);
    setShowFaqForm(true);
  };

  const handleDeleteFaq = async (id) => {
    if (window.confirm('آیا مطمئن هستید؟')) {
      const updatedFaqs = faqs.filter(f => f.id !== id);
      setFaqs(updatedFaqs);
      await saveContent('INFORMATION_FAQS', updatedFaqs, true);
    }
  };

  const handleAddSitemap = async (e) => {
    e.preventDefault();
    try {
      if (editingSitemapId) {
        const response = await sitemapLinkService.update(editingSitemapId, sitemapForm);
        setSitemapLinks(sitemapLinks.map(link => link.id === editingSitemapId ? response.data : link));
      } else {
        const response = await sitemapLinkService.create(sitemapForm);
        setSitemapLinks([...sitemapLinks, response.data]);
      }
      setSitemapForm({ category: '', label: '', path: '' });
      setEditingSitemapId(null);
      setShowSitemapForm(false);
      setSitemapError('');
    } catch (err) {
      setSitemapError('خطا در ذخیره لینک نقشه سایت');
    }
  };

  const handleEditSitemap = (link) => {
    setSitemapForm({
      category: link.category,
      label: link.label,
      path: link.path,
    });
    setEditingSitemapId(link.id);
    setShowSitemapForm(true);
  };

  const handleDeleteSitemap = async (id) => {
    if (window.confirm('آیا مطمئن هستید؟')) {
      try {
        await sitemapLinkService.delete(id);
        setSitemapLinks(sitemapLinks.filter(link => link.id !== id));
      } catch (err) {
        setSitemapError('خطا در حذف لینک');
      }
    }
  };

  const groupedSitemap = sitemapLinks.reduce((acc, link) => {
    if (!acc[link.category]) acc[link.category] = [];
    acc[link.category].push(link);
    return acc;
  }, {});

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-5xl font-bold mb-2">مدیریت منوی اطلاعات</h1>
              <p className="text-xl text-blue-100">ویرایش سوالات متداول، تماس، شکایات و حریم خصوصی</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition"
            >
              <LogOut size={20} />
              خروج
            </button>
          </div>
        </div>
      </div>

      <AdminNav />

      <div className="max-w-7xl mx-auto px-4 py-16">
        {/* Contact Information Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">اطلاعات تماس</h2>
          {contactError && <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">{contactError}</div>}

          {editingContact ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">ایمیل</label>
                <input
                  type="email"
                  value={tempContact.email}
                  onChange={(e) => setTempContact({ ...tempContact, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">تلفن</label>
                <input
                  type="tel"
                  value={tempContact.phone}
                  onChange={(e) => setTempContact({ ...tempContact, phone: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">آدرس</label>
                <input
                  type="text"
                  value={tempContact.address}
                  onChange={(e) => setTempContact({ ...tempContact, address: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">ساعات کاری</label>
                <input
                  type="text"
                  value={tempContact.workingHours}
                  onChange={(e) => setTempContact({ ...tempContact, workingHours: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>

              <div className="flex gap-4">
                <button
                  onClick={handleSaveContact}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition"
                >
                  ذخیره
                </button>
                <button
                  onClick={handleCancelContact}
                  className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition"
                >
                  انصراف
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3">
                  <Mail className="text-blue-600" size={20} />
                  <span className="text-gray-700">{contactInfo.email}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="text-blue-600" size={20} />
                  <span className="text-gray-700">{contactInfo.phone}</span>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="text-blue-600" size={20} />
                  <span className="text-gray-700">{contactInfo.address}</span>
                </div>
                <div className="flex items-center gap-3">
                  <FileText className="text-blue-600" size={20} />
                  <span className="text-gray-700">{contactInfo.workingHours}</span>
                </div>
              </div>
              <button
                onClick={() => {
                  setEditingContact(true);
                  setTempContact(contactInfo);
                }}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition"
              >
                <Edit2 size={18} />
                ویرایش اطلاعات تماس
              </button>
            </div>
          )}
        </div>

        {/* FAQs Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-12">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-gray-900">سوالات متداول</h2>
            {!showFaqForm && (
              <button
                onClick={() => setShowFaqForm(true)}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-bold hover:from-green-700 hover:to-green-800 transition"
              >
                <Plus size={20} />
                افزودن سوال جدید
              </button>
            )}
          </div>

          {/* FAQ Form */}
          {showFaqForm && (
            <div className="bg-gray-50 rounded-lg p-6 mb-8 border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingFaqId ? 'ویرایش سوال' : 'افزودن سوال جدید'}
                </h3>
                <button
                  onClick={() => {
                    setShowFaqForm(false);
                    setEditingFaqId(null);
                  }}
                  className="p-2 hover:bg-gray-200 rounded-lg transition"
                >
                  <X size={24} className="text-gray-600" />
                </button>
              </div>

              <form onSubmit={handleAddFaq} className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">سوال</label>
                  <input
                    type="text"
                    value={faqForm.question}
                    onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    placeholder="سوال را وارد کنید"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">پاسخ</label>
                  <textarea
                    value={faqForm.answer}
                    onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })}
                    required
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    placeholder="پاسخ را وارد کنید"
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition"
                  >
                    {editingFaqId ? 'ذخیره تغییرات' : 'افزودن سوال'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowFaqForm(false);
                      setEditingFaqId(null);
                    }}
                    className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition"
                  >
                    انصراف
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* FAQs List */}
          <div className="space-y-3">
            {faqs.map(faq => (
              <div key={faq.id} className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                <div className="flex items-center justify-between p-4 hover:bg-gray-100 transition">
                  <h3 className="font-bold text-gray-900 flex-1">{faq.question}</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditFaq(faq)}
                      className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteFaq(faq.id)}
                      className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                {faq.expanded && (
                  <div className="px-4 py-3 bg-white border-t border-gray-200">
                    <p className="text-gray-700">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Sitemap Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-12">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <Map className="text-blue-700" size={28} />
              <h2 className="text-3xl font-bold text-gray-900">نقشه سایت</h2>
            </div>
            {!showSitemapForm && (
              <button
                onClick={() => setShowSitemapForm(true)}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-bold hover:from-green-700 hover:to-green-800 transition"
              >
                <Plus size={20} />
                افزودن لینک
              </button>
            )}
          </div>

          {sitemapError && <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">{sitemapError}</div>}

          {loadingSitemap ? (
            <div className="text-center py-8 text-gray-500">در حال بارگذاری...</div>
          ) : (
            <>
              {showSitemapForm && (
                <form onSubmit={handleAddSitemap} className="bg-gray-50 rounded-lg p-6 mb-8 border border-gray-200">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-900">{editingSitemapId ? 'ویرایش لینک' : 'افزودن لینک جدید'}</h3>
                    <button
                      onClick={() => {
                        setShowSitemapForm(false);
                        setEditingSitemapId(null);
                        setSitemapForm({ category: '', label: '', path: '' });
                      }}
                      type="button"
                      className="p-2 hover:bg-gray-200 rounded-lg transition"
                    >
                      <X size={24} className="text-gray-600" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">دسته‌بندی</label>
                      <input
                        type="text"
                        value={sitemapForm.category}
                        onChange={(e) => setSitemapForm({ ...sitemapForm, category: e.target.value })}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                        placeholder="مثلاً: صفحات اصلی"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">عنوان</label>
                      <input
                        type="text"
                        value={sitemapForm.label}
                        onChange={(e) => setSitemapForm({ ...sitemapForm, label: e.target.value })}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                        placeholder="مثلاً: تماس با ما"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">مسیر</label>
                      <input
                        type="text"
                        value={sitemapForm.path}
                        onChange={(e) => setSitemapForm({ ...sitemapForm, path: e.target.value })}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                        placeholder="/contact"
                      />
                    </div>
                  </div>
                  <div className="flex gap-4 mt-6">
                    <button type="submit" className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition">
                      {editingSitemapId ? 'ذخیره تغییرات' : 'افزودن لینک'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowSitemapForm(false);
                        setEditingSitemapId(null);
                        setSitemapForm({ category: '', label: '', path: '' });
                      }}
                      className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition"
                    >
                      انصراف
                    </button>
                  </div>
                </form>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {Object.entries(groupedSitemap).length ? Object.entries(groupedSitemap).map(([category, links]) => (
                  <div key={category} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-700">{category}</h3>
                    <ul className="space-y-2">
                      {links.map(link => (
                        <li key={link.id} className="flex items-center justify-between p-2 bg-white rounded-lg">
                          <div>
                            <p className="font-semibold text-gray-900">{link.label}</p>
                            <p className="text-sm text-gray-500">{link.path}</p>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => handleEditSitemap(link)} className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"><Edit2 size={16} /></button>
                            <button onClick={() => handleDeleteSitemap(link.id)} className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"><Trash2 size={16} /></button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )) : (
                  <div className="col-span-full text-center py-8 text-gray-500">
                    <p>هیچ لینکی برای نمایش وجود ندارد</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Privacy Policy Section */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">حریم خصوصی</h2>

          {editingPrivacy ? (
            <div className="space-y-4">
              <textarea
                value={tempPrivacy}
                onChange={(e) => setTempPrivacy(e.target.value)}
                rows="10"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 font-medium"
              />
              <div className="flex gap-4">
                <button
                  onClick={handleSavePrivacy}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition"
                >
                  ذخیره
                </button>
                <button
                  onClick={handleCancelPrivacy}
                  className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition"
                >
                  انصراف
                </button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-gray-700 leading-relaxed mb-6 text-lg">{privacyPolicy}</p>
              <button
                onClick={() => {
                  setEditingPrivacy(true);
                  setTempPrivacy(privacyPolicy);
                }}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition"
              >
                <Edit2 size={18} />
                ویرایش حریم خصوصی
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminInformation;
